import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Button, Col, Descriptions, Divider, Empty, Form, InputNumber, Modal,
  Popconfirm, Row, Select, Space, Switch, Table, Tabs, Tag, message,
} from "antd";

import Water, { waitForJob } from "../../../api/Water";
import ImagePicker, { COLORS } from "./ImagePicker";
import SurveyModal, { EMPTY_SURVEY, remapSurvey } from "./SurveyModal";
import {
  imageToOrtho, orthoToImage, reprojectionErrors, solveHomography, suggestRange,
} from "./homography";

// 計算參數設定。
//
// 設計重點是「所有參數都能改，而且改完立刻看得到結果」：
//   * GRP、水尺線、PIV 格網都在影像上點，不手打像素座標
//   * 重投影誤差即時計算（用與後端相同的演算法，數字一致）
//   * 試算：用尚未儲存的草稿參數跑一次真的計算，回傳數值、品質指標與疊圖
//
// 舊程式把這些全部寫死在 main_D4.py / main_D5.py，換一個測站就得改程式；
// 而且 D4 的 PIV 格網有三分之二落在對岸的卵石上，沒有人發現 —— 因為沒有
// 任何地方看得到格網畫在哪裡。

// [0, 0] 代表尚未設定 —— onAdd 一直用這個約定決定要填哪一個欄位，顯示端
// 也必須一致。否則未設定的點會被畫在正射原點那一角，看起來像是已經設定好
// 了，而且會被算進點數。
//
// 放在模組層級而不是元件內：它是純函式，留在元件裡只會讓 useMemo 的依賴
// 陣列多一個每次 render 都變的項目。
const isSet = (ortho) => !!(ortho && (ortho[0] || ortho[1]));

const MAX_GRPS = 12;

// 正射影像超過這個像素數就提示。water 端用 warpPerspective 一次產生整張圖，
// 舊參數的範圍在 0.01 m/px 下是 65141×19871（約 13 億像素），記憶體直接爆掉。
const MAX_ORTHO_PIXELS = 20e6;

const fin = (v) => typeof v === "number" && Number.isFinite(v);

// GRP 的狀態：
//   ok        可參與計算
//   unpicked  尚未在影像上指認（px/py 為 null）
//   outside   影像座標落在目前參考影像之外 —— 鏡頭移位、或換了解析度
//             （D4 從 1920×1080 改成 704×480）後沿用的舊座標就是這種情形。
//             這些點畫不出來，若照樣算進 homography，整組轉換都會是錯的。
//   noxy      影像上點了，但還沒填實測座標
//
// X/Y 是現場測量的成果，鏡頭怎麼移都不會變；會失效的只有 px/py。所以
// 「重新指認」只清 px/py，不碰 X/Y。
//
// 未指認與待實測的點可以先存（常見流程是先在影像上標點、之後才去現地量），
// 但不能試算、手動計算或啟用排程 —— 後端 water.py 的 _incomplete_grps()
// 會擋同樣的三條路。畫面外的點例外：它們有數字，後端分辨不出是舊座標，
// 所以前端在儲存時就擋下。
function grpState(g, size) {
  if (!g || !fin(g.px) || !fin(g.py)) return "unpicked";
  if (size && (g.px < 0 || g.py < 0 || g.px > size.w || g.py > size.h)) {
    return "outside";
  }
  if (!fin(g.X) || !fin(g.Y)) return "noxy";
  return "ok";
}

const needsPick = (state) => state === "unpicked" || state === "outside";

const STATE_TAG = {
  unpicked: { text: "未指認", color: "default" },
  outside: { text: "畫面外", color: "red" },
  noxy: { text: "待實測", color: "orange" },
};

// 704×480 的畫面上，3 px 以內算好、8 px 以上通常是點錯或量錯
const errLevel = (e) => (!Number.isFinite(e) ? null
  : e > 8 ? "bad" : e > 3 ? "warn" : "good");

const POINT_COLOR = { bad: "#ff3b30", warn: "#ff9500", good: "#34c759" };
const TEXT_COLOR = { bad: "#d4380d", warn: "#d46b08" };

const DEFAULTS = {
  schema: 2,
  lens: { enabled: false, k1: 0, k2: 0, p1: 0, p2: 0 },
  ortho: { grps: [], range: { xmin: 0, xmax: 0, ymin: 0, ymax: 0 },
           resolution: 0.01 },
  waterlevel: { source: "none", method: "ruler_line",
                p1: [0, 0], p1_elev: 0, p2: [0, 0], p2_elev: 0,
                band_px: 40, min_contrast: 15, blur: 5, min_run: 10,
                external: { url: null, manual_value: null } },
  piv: { origin: [0, 0], along: [0, 0], across: [0, 0],
         n_along: 15, n_across: 5, window: 64, search: 10,
         frame_step: 1, min_corr: 0.5 },
  velocity: { alpha: 0, h0: 0, surface_coef: 1 },
  discharge: { mode: "polynomial", k: 1, coef: [0, 0, 0, 0], negative: "null" },
  quality: { min_valid_ratio: 0.3, min_mean_corr: 0.4, min_disp_px: 0.5 },
  // 「由距離換算座標」的現場量測資料。water 計算時用不到，存著只是為了
  // 之後補量或檢查時不必重新輸入
  survey: EMPTY_SURVEY,
};

function merge(base, override) {
  const out = JSON.parse(JSON.stringify(base));
  Object.entries(override || {}).forEach(([key, value]) => {
    if (value && typeof value === "object" && !Array.isArray(value)
        && out[key] && typeof out[key] === "object" && !Array.isArray(out[key])) {
      out[key] = merge(out[key], value);
    } else if (value !== undefined) {
      out[key] = value;
    }
  });
  return out;
}

export default function WaterConfig({ machineID, station, open, onClose, onSaved }) {
  const [params, setParams] = useState(DEFAULTS);
  const [schedule, setSchedule] = useState({});
  const [reference, setReference] = useState(null);   // 參考影像的網址
  const [imgSize, setImgSize] = useState(null);       // 參考影像實際尺寸 {w, h}
  const [busy, setBusy] = useState(null);             // 'reference' | 'preview' | 'save'
  const [preview, setPreview] = useState(null);       // 試算結果的 job
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("ortho");
  const [dirty, setDirty] = useState(false);
  // 正在指認的 GRP 索引：設定後，下一次點影像會寫進這一列，而不是新增一點
  const [pending, setPending] = useState(null);
  const [surveyOpen, setSurveyOpen] = useState(false);

  // -- 載入 ----------------------------------------------------------
  const load = useCallback(async () => {
    const [config, jobs, versions] = await Promise.all([
      Water.getConfig(machineID),
      Water.listJobs(machineID, { type: "reference", limit: 5 }),
      Water.getConfigHistory(machineID, 20),
    ]);
    if (config.ok) {
      setParams(merge(DEFAULTS, config.data.params));
      setSchedule({
        enabled: !!config.data.enabled,
        interval_min: config.data.interval_min,
        offset_min: config.data.offset_min,
        capture_seconds: config.data.capture_seconds,
        retention_days: config.data.retention_days,
      });
    }
    if (jobs.ok) {
      const usableJob = jobs.data.find(
        (job) => job.status === "done" && job.media?.reference);
      setReference(usableJob ? usableJob.media.reference : null);
    }
    if (versions.ok) setHistory(versions.data);
    setPending(null);
    setDirty(false);
  }, [machineID]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // 參考影像的實際尺寸，用來判斷 GRP 是否落在畫面外。ImagePicker 內部也會
  // 量，但那是它自己的狀態；這裡需要在表格與計算時就知道。瀏覽器有快取，
  // 不會真的下載兩次。
  useEffect(() => {
    if (!reference) { setImgSize(null); return undefined; }
    let alive = true;
    const img = new Image();
    img.onload = () => {
      if (alive) setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
    };
    img.src = reference;
    return () => { alive = false; };
  }, [reference]);

  const update = (path, value) => {
    setParams((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let node = next;
      keys.slice(0, -1).forEach((key) => { node = node[key]; });
      node[keys[keys.length - 1]] = value;
      return next;
    });
    setDirty(true);
  };

  // -- 幾何 ----------------------------------------------------------
  const grps = params.ortho.grps;
  const states = useMemo(
    () => grps.map((g) => grpState(g, imgSize)), [grps, imgSize]);
  // 只有狀態 ok 的點參與 homography；畫面外的舊座標不能混進來
  const usable = useMemo(
    () => grps.filter((_, i) => states[i] === "ok"), [grps, states]);
  const H = useMemo(() => solveHomography(usable), [usable]);
  // 誤差對回原本的列（不可用的點為 null）
  const errors = useMemo(() => {
    const list = reprojectionErrors(usable, H);
    let k = 0;
    return states.map((state) => (state === "ok" ? list[k++] : null));
  }, [states, usable, H]);
  const rmse = useMemo(() => {
    const valid = errors.filter((e) => Number.isFinite(e));
    return valid.length >= 4
      ? Math.sqrt(valid.reduce((sum, e) => sum + e * e, 0) / valid.length)
      : null;
  }, [errors]);

  const { range, resolution } = params.ortho;
  const toImg = useCallback(
    (ortho) => orthoToImage(H, range, resolution, ortho[0], ortho[1]),
    [H, range, resolution]);
  const toOrtho = useCallback(
    (point) => imageToOrtho(H, range, resolution, point.x, point.y),
    [H, range, resolution]);

  const orthoW = Math.round((range.xmax - range.xmin) / resolution) + 1;
  const orthoH = Math.round((range.ymax - range.ymin) / resolution) + 1;
  const orthoTooBig = orthoW * orthoH > MAX_ORTHO_PIXELS;

  // PIV 格點：三個角點展開成格網，再投影回影像上顯示
  const pivPoints = useMemo(() => {
    if (!H) return [];
    const { origin, along, across, n_along, n_across } = params.piv;
    if (!n_along || !n_across) return [];
    // 三個角都設定好才畫格網。少一個就會退化成一條線或擠在原點的一團，
    // 看起來像是格網算錯了，其實只是還沒點完。
    if (![origin, along, across].every(isSet)) return [];
    const points = [];
    for (let k = 0; k < n_across; k += 1) {
      for (let i = 0; i < n_along; i += 1) {
        const ox = origin[0] + (along[0] - origin[0]) / n_along * i
                             + (across[0] - origin[0]) / n_across * k;
        const oy = origin[1] + (along[1] - origin[1]) / n_along * i
                             + (across[1] - origin[1]) / n_across * k;
        const img = toImg([ox, oy]);
        if (img) points.push(img);
      }
    }
    return points;
  }, [H, params.piv, toImg]);

  // -- GRP 操作 -------------------------------------------------------
  const setGrps = (next) => update("ortho.grps", next);

  const withPixel = (list, index, point) => {
    const next = [...list];
    next[index] = { ...next[index], px: point.x, py: point.y };
    return next;
  };

  // 從 from 的下一列往後找還需要指認的點，找不到就從頭找
  const nextPending = (list, from) => {
    for (let k = 1; k <= list.length; k += 1) {
      const i = (from + k) % list.length;
      if (needsPick(grpState(list[i], imgSize))) return i;
    }
    return null;
  };

  // 刪點時，距離量測裡的點編號要跟著往前遞補，否則 #5 的距離會變成 #4 的
  const removeGrp = (index) => {
    setGrps(grps.filter((_, i) => i !== index));
    update("survey", remapSurvey(params.survey, index));
    setPending((p) => (p === null || p === index ? null : p > index ? p - 1 : p));
  };

  const repickAll = () => {
    const next = grps.map((g) => ({ ...g, px: null, py: null }));
    setGrps(next);
    setPending(next.length ? 0 : null);
  };

  const removeAll = () => {
    setGrps([]);
    update("survey", { ...EMPTY_SURVEY, x0: params.survey?.x0 ?? 0,
                       y0: params.survey?.y0 ?? 0 });
    setPending(null);
  };

  // 影像位置給距離換算判斷左右方向用；畫面外的舊座標不可靠，不提供
  const surveyPixels = useMemo(() => grps.map((g, i) => (
    states[i] === "ok" || states[i] === "noxy" ? { px: g.px, py: g.py } : null
  )), [grps, states]);

  const applySurvey = (coords) => {
    const keys = Object.keys(coords).map(Number);
    const overwritten = keys.filter((k) => {
      const g = grps[k];
      return fin(g.X) && fin(g.Y)
        && (Math.abs(g.X - coords[k].X) > 0.0005 || Math.abs(g.Y - coords[k].Y) > 0.0005);
    });
    const doApply = () => {
      const next = grps.map((g, k) => (coords[k]
        ? { ...g, X: coords[k].X, Y: coords[k].Y } : g));
      setGrps(next);
      setSurveyOpen(false);
      message.success(`已填入 ${keys.length} 點的實測座標`);
    };
    if (!overwritten.length) {
      doApply();
      return;
    }
    Modal.confirm({
      title: "覆蓋現有座標？",
      content: `第 ${overwritten.map((k) => k + 1).join("、")} 點已經有不同的 X/Y，`
        + "套用後會被換算結果取代。兩套座標不能混用，請確認這些舊值不是另一套測量的成果。",
      okText: "覆蓋", cancelText: "取消",
      onOk: doApply,
    });
  };

  const countState = (state) => states.filter((s) => s === state).length;
  // 尚未完成、但允許先存起來的點
  const incompleteCount = () => countState("unpicked") + countState("noxy");

  // 試算前：所有 GRP 都必須完整。畫面外的點尤其危險 —— water 端會拿舊座標
  // 算出一組錯的轉換，而且不會報錯，只會給出一個看起來正常的流速
  const grpProblem = () => {
    const bad = states.filter((state) => state !== "ok").length;
    return bad
      ? `還有 ${bad} 個 GRP 未指認、在畫面外或待實測，補齊或刪除後才能試算`
      : null;
  };

  // 儲存前：只擋畫面外的點（理由見 grpState 上方說明）；排程啟用時則要求
  // 全部完整，否則下一次排程就會失敗
  const saveProblem = () => {
    const outside = countState("outside");
    if (outside) {
      return `有 ${outside} 點在畫面外，請重新指認或刪除後再儲存`;
    }
    const incomplete = incompleteCount();
    if (incomplete && schedule.enabled) {
      return `排程啟用中，GRP 必須全部完成（還有 ${incomplete} 點未完成）。先到「排程 / 版本」關閉排程，或補齊後再儲存`;
    }
    return null;
  };

  // -- 動作 ----------------------------------------------------------
  const grabReference = async () => {
    setBusy("reference");
    const created = await Water.createJob(machineID, "reference");
    if (!created.ok) {
      setBusy(null);
      message.warning(created.error);
      return;
    }
    message.loading({ content: "擷取參考影像中…", key: "ref", duration: 0 });
    const done = await waitForJob(created.data.job_id);
    setBusy(null);
    if (done.ok && done.data.status === "done" && done.data.media?.reference) {
      setReference(`${done.data.media.reference}?b=${Date.now()}`);
      message.success({ content: "已取得參考影像", key: "ref" });
    } else {
      message.error({ content: done.data?.error || "擷取失敗", key: "ref" });
    }
  };

  const runPreview = async () => {
    const problem = grpProblem();
    if (problem) {
      message.error(problem);
      setTab("ortho");
      return;
    }
    setBusy("preview");
    setPreview(null);
    const created = await Water.createJob(machineID, "preview", params);
    if (!created.ok) {
      setBusy(null);
      message.warning(created.error
        + (created.details ? `：${created.details.join("；")}` : ""));
      return;
    }
    message.loading({ content: "試算中…", key: "pv", duration: 0 });
    const done = await waitForJob(created.data.job_id);
    setBusy(null);
    if (!done.ok) {
      message.error({ content: done.error, key: "pv" });
      return;
    }
    setPreview(done.data);
    setTab("preview");
    if (done.data.status === "done") {
      message.success({ content: "試算完成", key: "pv" });
    } else {
      message.error({ content: done.data.error || "試算未完成", key: "pv" });
    }
  };

  const save = async () => {
    const problem = saveProblem();
    if (problem) {
      message.error(problem);
      if (countState("outside")) setTab("ortho");
      return;
    }
    const incomplete = incompleteCount();
    setBusy("save");
    const result = await Water.updateConfig(machineID, { ...schedule, params });
    setBusy(null);
    if (!result.ok) {
      message.error(result.error
        + (result.details ? `：${result.details.join("；")}` : ""));
      return;
    }
    if (incomplete) {
      message.warning(`已儲存。還有 ${incomplete} 點未指認或待實測，補齊前無法試算或啟用排程`);
    } else {
      message.success("已儲存");
    }
    setDirty(false);
    load();
    if (onSaved) onSaved();
  };

  const restore = (version) => {
    Modal.confirm({
      title: "還原參數",
      content: `將編輯中的參數換成 ${new Date(version.saved_at).toLocaleString()} 的版本？尚未儲存的修改會遺失。`,
      onOk: () => {
        setParams(merge(DEFAULTS, version.params));
        setPending(null);
        setDirty(true);
        message.info("已載入該版本，仍需按儲存才會生效");
      },
    });
  };

  // -- 各分頁 --------------------------------------------------------
  // 影像上只畫得出位置有效的點；ImagePicker 回傳的 index 是這個陣列的位置，
  // 要透過 visible[k].i 對回原本的 GRP 列
  const visible = grps
    .map((g, i) => ({ g, i }))
    .filter(({ i }) => !needsPick(states[i]));

  const grpPoints = visible.map(({ g, i }) => ({
    x: g.px, y: g.py,
    label: `${i + 1}${Number.isFinite(errors[i]) ? ` (${errors[i].toFixed(1)})` : ""}`,
    color: POINT_COLOR[errLevel(errors[i])] || COLORS.grp,
  }));

  const outsideCount = countState("outside");
  const unpickedCount = countState("unpicked");
  const noxyCount = countState("noxy");
  const pendingGrp = pending !== null ? grps[pending] : null;
  const coord = (v) => (fin(v) ? v : "?");

  const orthoTab = (
    <Row gutter={12}>
      <Col flex="auto">
        {reference ? (
          <ImagePicker
            src={reference} points={grpPoints}
            task={pendingGrp
              ? `指認 #${pending + 1}：點選實測座標 X ${coord(pendingGrp.X)}、Y ${coord(pendingGrp.Y)} 的地物在畫面上的位置`
              : "標定 GRP：點選畫面上的固定地物（像素位置自動記錄），再於表格填入該地物的現場實測 X / Y"}
            needPoints={Math.max(4, grps.length)}
            maxPoints={pending !== null ? Infinity : MAX_GRPS}
            onAdd={(point) => {
              if (pendingGrp) {
                const next = withPixel(grps, pending, point);
                setGrps(next);
                const following = nextPending(next, pending);
                setPending(following);
                if (following === null) message.success("所有 GRP 都已指認");
                return;
              }
              if (grps.length >= MAX_GRPS) {
                message.warning(`GRP 最多 ${MAX_GRPS} 點`);
                return;
              }
              setGrps([...grps, { X: null, Y: null, px: point.x, py: point.y }]);
            }}
            onMove={(k, point) => setGrps(withPixel(grps, visible[k].i, point))}
            onRemove={(k) => removeGrp(visible[k].i)}
          />
        ) : (
          <Empty description="尚無參考影像" style={{ padding: 60 }}>
            <Button type="primary" loading={busy === "reference"}
                    onClick={grabReference}>擷取參考影像</Button>
          </Empty>
        )}
      </Col>
      <Col flex="470px">
        <Space wrap size={[6, 6]} style={{ marginBottom: 8 }}>
          <Button size="small" loading={busy === "reference"}
                  onClick={grabReference}>重新擷取影像</Button>
          <Button size="small"
                  disabled={usable.length < 2}
                  onClick={() => {
                    const suggested = suggestRange(usable);
                    if (suggested) {
                      update("ortho.range", suggested);
                      message.info("已依有效 GRP 的範圍建議正射範圍");
                    }
                  }}>由 GRP 建議範圍</Button>
          <Button size="small" disabled={grps.length < 3}
                  title={grps.length < 3 ? "至少要有 3 個 GRP" : undefined}
                  onClick={() => setSurveyOpen(true)}>由距離換算座標</Button>
          <Popconfirm
            title="清除所有點的影像位置？"
            description="實際座標 X/Y 會保留，接著依序在影像上重新點選"
            okText="清除並開始" cancelText="取消"
            disabled={grps.length === 0 || !reference}
            onConfirm={repickAll}>
            <Button size="small" disabled={grps.length === 0 || !reference}>
              全部重新指認
            </Button>
          </Popconfirm>
          <Popconfirm
            title="刪除全部 GRP？"
            description="包含 X/Y 測量值，刪除後需重新輸入"
            okText="全部刪除" okButtonProps={{ danger: true }} cancelText="取消"
            disabled={grps.length === 0}
            onConfirm={removeAll}>
            <Button size="small" danger disabled={grps.length === 0}>
              全部刪除
            </Button>
          </Popconfirm>
        </Space>

        {(outsideCount > 0 || unpickedCount > 0 || noxyCount > 0) && (
          <Alert style={{ marginBottom: 8 }} showIcon
                 type={outsideCount ? "warning" : "info"}
                 message={[
                   outsideCount ? `${outsideCount} 點在畫面外` : null,
                   unpickedCount ? `${unpickedCount} 點未指認` : null,
                   noxyCount ? `${noxyCount} 點待實測` : null,
                 ].filter(Boolean).join("、")}
                 description={outsideCount
                   ? "畫面外的點多半是鏡頭移位或換解析度前的舊座標，不參與計算，也不能儲存。按「全部重新指認」或逐列按「指認」。"
                   : "可以先儲存，現地量測或指認完再補上；補齊前無法試算或啟用排程。"} />
        )}

        <Table size="small" rowKey={(_, index) => index} pagination={false}
               scroll={{ y: 240 }} dataSource={grps}
               rowClassName={(_, index) =>
                 index === pending ? "ant-table-row-selected" : ""}
               columns={[
                 { title: "#", width: 34,
                   render: (_, __, index) => index + 1 },
                 { title: "實測 X (m)", width: 84,
                   render: (_, row, index) => (
                     <InputNumber size="small" value={row.X} step={0.01}
                       placeholder="待實測" style={{ width: "100%" }}
                       onChange={(value) => {
                         const next = [...grps];
                         next[index] = { ...next[index], X: value };
                         setGrps(next);
                       }} />) },
                 { title: "實測 Y (m)", width: 84,
                   render: (_, row, index) => (
                     <InputNumber size="small" value={row.Y} step={0.01}
                       placeholder="待實測" style={{ width: "100%" }}
                       onChange={(value) => {
                         const next = [...grps];
                         next[index] = { ...next[index], Y: value };
                         setGrps(next);
                       }} />) },
                 { title: "誤差", width: 72, align: "right",
                   render: (_, __, index) => {
                     const tag = STATE_TAG[states[index]];
                     if (tag) {
                       return <Tag color={tag.color} style={{ marginRight: 0 }}>
                         {tag.text}</Tag>;
                     }
                     const e = errors[index];
                     return Number.isFinite(e)
                       ? <span style={{ color: TEXT_COLOR[errLevel(e)] }}>
                           {e.toFixed(1)}</span>
                       : "—";
                   } },
                 { title: "", width: 110,
                   render: (_, __, index) => (
                     <Space size={0}>
                       <Button size="small" disabled={!reference}
                               type={pending === index ? "primary" : "default"}
                               onClick={() =>
                                 setPending(pending === index ? null : index)}>
                         {pending === index ? "取消" : "指認"}
                       </Button>
                       <Button size="small" type="link" danger
                               onClick={() => removeGrp(index)}>刪除</Button>
                     </Space>) },
               ]} />

        <Alert style={{ marginTop: 8 }}
               type={rmse === null ? "info"
                 : rmse > 8 ? "error" : rmse > 3 ? "warning" : "success"}
               showIcon
               message={rmse === null
                 ? `需要至少 4 個有效 GRP（目前 ${usable.length} 個）`
                 : `重投影 RMSE ${rmse.toFixed(2)} px`}
               description={rmse !== null && rmse > 3
                 ? "目標是 3 px 以下。誤差特別大的點多半是點錯位置或量錯座標；也可能是 GRP 與水面高程差距過大（2D 投影假設兩者共平面），或廣角鏡頭的畸變。"
                 : undefined} />

        <Divider style={{ margin: "12px 0" }} orientation="left" plain>
          正射範圍
        </Divider>
        <Row gutter={8}>
          {["xmin", "xmax", "ymin", "ymax"].map((key) => (
            <Col span={12} key={key} style={{ marginBottom: 6 }}>
              <NumberField label={key} value={range[key]} step={0.5}
                onChange={(value) => update(`ortho.range.${key}`, value)} />
            </Col>
          ))}
          <Col span={12}>
            <NumberField label="解析度 m/px" value={resolution} step={0.005}
              min={0.001} max={1}
              onChange={(value) => update("ortho.resolution", value)} />
          </Col>
          <Col span={12}>
            <div style={{ fontSize: 11, paddingTop: 20,
                          color: orthoTooBig ? "#d4380d" : "var(--secondary-text)" }}>
              正射影像 {orthoW}×{orthoH} px
              {orthoTooBig && "（過大：縮小範圍或把解析度數值調大）"}
            </div>
          </Col>
        </Row>
      </Col>
    </Row>
  );

  const waterlevelTab = (
    <Row gutter={12}>
      <Col flex="auto">
        {reference && H ? (
          <ImagePicker
            src={reference}
            task="畫水尺線：先點上端、再點下端"
            needPoints={2}
            points={[params.waterlevel.p1, params.waterlevel.p2]
              .map((ortho, index) => {
                if (!isSet(ortho)) return null;
                const img = toImg(ortho);
                return img ? { x: img.x, y: img.y, label: index === 0 ? "上" : "下",
                               color: COLORS.line } : null;
              }).filter(Boolean)}
            lines={(() => {
              if (!isSet(params.waterlevel.p1) || !isSet(params.waterlevel.p2)) {
                return [];
              }
              const a = toImg(params.waterlevel.p1);
              const b = toImg(params.waterlevel.p2);
              return a && b ? [{ from: a, to: b, color: COLORS.line }] : [];
            })()}
            onAdd={(point) => {
              const ortho = toOrtho(point);
              if (!ortho) return;
              const empty = !params.waterlevel.p1[0] && !params.waterlevel.p1[1];
              update(empty ? "waterlevel.p1" : "waterlevel.p2",
                     [Math.round(ortho.x), Math.round(ortho.y)]);
            }}
            onMove={(index, point) => {
              const ortho = toOrtho(point);
              if (!ortho) return;
              update(index === 0 ? "waterlevel.p1" : "waterlevel.p2",
                     [Math.round(ortho.x), Math.round(ortho.y)]);
            }}
            onRemove={(index) =>
              update(index === 0 ? "waterlevel.p1" : "waterlevel.p2", [0, 0])}
          />
        ) : (
          <Empty style={{ padding: 60 }}
                 description={!reference ? "尚無參考影像"
                   : "需要先設定至少 4 個有效 GRP"} />
        )}
      </Col>
      <Col flex="340px">
        <Form layout="vertical" size="small">
          <Form.Item label="水位來源">
            <Select value={params.waterlevel.source}
                    onChange={(value) => update("waterlevel.source", value)}
                    options={[
                      { value: "none", label: "不計算水位（只出流速）" },
                      { value: "image", label: "由影像偵測" },
                      { value: "external", label: "外部提供（人工或鄰近測站）" },
                    ]} />
          </Form.Item>
          {params.waterlevel.source === "none" && (
            <Alert type="info" showIcon
                   message="沒有水位就無法計算流量，流量會留空。現場尚未設置水尺時用這個模式。" />
          )}
          {params.waterlevel.source === "external" && (
            <NumberField label="人工填入水位 (m)"
              value={params.waterlevel.external.manual_value} step={0.01}
              onChange={(value) =>
                update("waterlevel.external.manual_value", value)} />
          )}
          {params.waterlevel.source === "image" && (
            <>
              <Row gutter={8}>
                <Col span={12}>
                  <NumberField label="上端高程 (m)" step={0.01}
                    value={params.waterlevel.p1_elev}
                    onChange={(value) => update("waterlevel.p1_elev", value)} />
                </Col>
                <Col span={12}>
                  <NumberField label="下端高程 (m)" step={0.01}
                    value={params.waterlevel.p2_elev}
                    onChange={(value) => update("waterlevel.p2_elev", value)} />
                </Col>
              </Row>
              <Divider style={{ margin: "10px 0" }} orientation="left" plain>
                進階
              </Divider>
              <Row gutter={8}>
                <Col span={12}>
                  <NumberField label="取樣帶寬 (px)" value={params.waterlevel.band_px}
                    onChange={(value) => update("waterlevel.band_px", value)} />
                </Col>
                <Col span={12}>
                  <NumberField label="最低對比" value={params.waterlevel.min_contrast}
                    onChange={(value) => update("waterlevel.min_contrast", value)} />
                </Col>
                <Col span={12}>
                  <NumberField label="平滑核" value={params.waterlevel.blur}
                    onChange={(value) => update("waterlevel.blur", value)} />
                </Col>
                <Col span={12}>
                  <NumberField label="持續格數" value={params.waterlevel.min_run}
                    onChange={(value) => update("waterlevel.min_run", value)} />
                </Col>
              </Row>
              <Alert style={{ marginTop: 8 }} type="info"
                     message="水尺線要跨越水岸交界，兩端各填該點的實際高程；水線位置由兩端內插得出。" />
            </>
          )}
        </Form>
      </Col>
    </Row>
  );

  const pivTab = (
    <Row gutter={12}>
      <Col flex="auto">
        {reference && H ? (
          <ImagePicker
            src={reference}
            task="定義 PIV 格網：依序點 起點 → 順流方向 → 橫向"
            needPoints={3}
            grid={pivPoints}
            points={["origin", "along", "across"].map((key, index) => {
              if (!isSet(params.piv[key])) return null;
              const img = toImg(params.piv[key]);
              return img ? {
                x: img.x, y: img.y,
                label: ["起點", "順流", "橫向"][index],
                color: index === 0 ? COLORS.origin : COLORS.grid,
              } : null;
            }).filter(Boolean)}
            lines={(() => {
              const o = isSet(params.piv.origin) ? toImg(params.piv.origin) : null;
              const a = isSet(params.piv.along) ? toImg(params.piv.along) : null;
              const c = isSet(params.piv.across) ? toImg(params.piv.across) : null;
              const out = [];
              if (o && a) out.push({ from: o, to: a, color: COLORS.grid });
              if (o && c) out.push({ from: o, to: c, color: COLORS.grid,
                                     dashed: true });
              return out;
            })()}
            onAdd={(point) => {
              const ortho = toOrtho(point);
              if (!ortho) return;
              const order = ["origin", "along", "across"];
              const next = order.find(
                (key) => !params.piv[key][0] && !params.piv[key][1]) || "across";
              update(`piv.${next}`, [Math.round(ortho.x), Math.round(ortho.y)]);
            }}
            onMove={(index, point) => {
              const ortho = toOrtho(point);
              if (!ortho) return;
              update(`piv.${["origin", "along", "across"][index]}`,
                     [Math.round(ortho.x), Math.round(ortho.y)]);
            }}
            onRemove={(index) =>
              update(`piv.${["origin", "along", "across"][index]}`, [0, 0])}
          />
        ) : (
          <Empty style={{ padding: 60 }}
                 description={!reference ? "尚無參考影像"
                   : "需要先設定至少 4 個有效 GRP"} />
        )}
      </Col>
      <Col flex="340px">
        <Row gutter={8}>
          <Col span={12}>
            <NumberField label="順流格點數" value={params.piv.n_along} min={1}
              onChange={(value) => update("piv.n_along", value)} />
          </Col>
          <Col span={12}>
            <NumberField label="橫向格點數" value={params.piv.n_across} min={1}
              onChange={(value) => update("piv.n_across", value)} />
          </Col>
          <Col span={12}>
            <NumberField label="比對幀距" value={params.piv.frame_step} min={1}
              onChange={(value) => update("piv.frame_step", value)} />
          </Col>
          <Col span={12}>
            <NumberField label="最低相關" value={params.piv.min_corr} step={0.05}
              min={0} max={1}
              onChange={(value) => update("piv.min_corr", value)} />
          </Col>
          <Col span={12}>
            <NumberField label="判讀窗 (px)" value={params.piv.window} min={8}
              onChange={(value) => update("piv.window", value)} />
          </Col>
          <Col span={12}>
            <NumberField label="搜尋半徑 (px)" value={params.piv.search} min={2}
              onChange={(value) => update("piv.search", value)} />
          </Col>
        </Row>
        <Alert style={{ marginTop: 10 }} type="info" showIcon
               message="格點必須落在水面上"
               description="試算後看「位移中位數」：理想值 5–15 px。太小表示幀距不足（低解析度來源常見），把比對幀距調大；太大則容易超出搜尋半徑。" />
        <div style={{ marginTop: 8, fontSize: 12 }}>
          目前共 {params.piv.n_along * params.piv.n_across} 個格點
        </div>
      </Col>
    </Row>
  );

  const flowTab = (
    <Row gutter={16}>
      <Col span={12}>
        <Divider orientation="left" plain>流速</Divider>
        <Row gutter={8}>
          <Col span={8}>
            <NumberField label="門檻係數 α" value={params.velocity.alpha}
              step={0.1} onChange={(value) => update("velocity.alpha", value)} />
          </Col>
          <Col span={8}>
            <NumberField label="基準水位 H₀" value={params.velocity.h0}
              step={0.1} onChange={(value) => update("velocity.h0", value)} />
          </Col>
          <Col span={8}>
            <NumberField label="表面係數" value={params.velocity.surface_coef}
              step={0.05} min={0.1} max={1.5}
              onChange={(value) => update("velocity.surface_coef", value)} />
          </Col>
        </Row>
        <div style={{ fontSize: 12, color: "var(--secondary-text)", marginTop: 4 }}>
          低於 max(0, α·(H − H₀)) 的流速視為靜水誤判而剔除；α = 0 表示不過濾。
          表面係數是表面流速換算斷面平均流速的經驗值，慣用 0.8–0.9。
        </div>
      </Col>
      <Col span={12}>
        <Divider orientation="left" plain>流量</Divider>
        <div style={{ fontSize: 12, marginBottom: 6 }}>
          Q = k · V · (c₃H³ + c₂H² + c₁H + c₀)
        </div>
        <Row gutter={8}>
          {[0, 1, 2, 3].map((index) => (
            <Col span={6} key={index}>
              <NumberField label={`c${3 - index}`} value={params.discharge.coef[index]}
                step={0.0001}
                onChange={(value) => {
                  const next = [...params.discharge.coef];
                  next[index] = value;
                  update("discharge.coef", next);
                }} />
            </Col>
          ))}
          <Col span={8} style={{ marginTop: 6 }}>
            <NumberField label="k" value={params.discharge.k} step={0.1}
              onChange={(value) => update("discharge.k", value)} />
          </Col>
          <Col span={16} style={{ marginTop: 6 }}>
            <div style={{ fontSize: 12, marginBottom: 2 }}>負值處理</div>
            <Select size="small" style={{ width: "100%" }}
                    value={params.discharge.negative}
                    onChange={(value) => update("discharge.negative", value)}
                    options={[
                      { value: "null", label: "留空（建議）" },
                      { value: "zero", label: "歸零" },
                      { value: "keep", label: "照實存" },
                    ]} />
          </Col>
        </Row>
        <Alert style={{ marginTop: 10 }} type="warning" showIcon
               message="率定曲線與攝影機視角綁定"
               description="鏡頭移位或斷面改變後必須重新率定。沿用舊係數算出來的數字看起來正常，但沒有物理意義 —— D4 在 2024 年記錄的 84 筆流量全部為負，就是因為水位始終低於該曲線的有效範圍。" />
      </Col>
    </Row>
  );

  const scheduleTab = (
    <Row gutter={16}>
      <Col span={12}>
        <Form layout="vertical" size="small">
          <Form.Item label="啟用排程">
            <Space>
              <Switch checked={schedule.enabled}
                      onChange={(value) => {
                        const left = states.filter((s) => s !== "ok").length;
                        if (value && left) {
                          message.warning(`還有 ${left} 個 GRP 未完成，補齊後才能啟用排程`);
                          return;
                        }
                        setSchedule((prev) => ({ ...prev, enabled: value }));
                        setDirty(true);
                      }} />
              <span style={{ fontSize: 12, color: "var(--secondary-text)" }}>
                參數設定完成並試算確認後再啟用
              </span>
            </Space>
          </Form.Item>
          <Row gutter={8}>
            <Col span={12}>
              <NumberField label="計算頻率 (分)" value={schedule.interval_min}
                min={1}
                onChange={(value) => {
                  setSchedule((prev) => ({ ...prev, interval_min: value }));
                  setDirty(true);
                }} />
            </Col>
            <Col span={12}>
              <NumberField label="錯開分鐘" value={schedule.offset_min} min={0}
                onChange={(value) => {
                  setSchedule((prev) => ({ ...prev, offset_min: value }));
                  setDirty(true);
                }} />
            </Col>
            <Col span={12}>
              <NumberField label="錄影秒數" value={schedule.capture_seconds}
                min={2} max={60}
                onChange={(value) => {
                  setSchedule((prev) => ({ ...prev, capture_seconds: value }));
                  setDirty(true);
                }} />
            </Col>
            <Col span={12}>
              <NumberField label="影像保留天數" value={schedule.retention_days}
                min={0}
                onChange={(value) => {
                  setSchedule((prev) => ({ ...prev, retention_days: value }));
                  setDirty(true);
                }} />
            </Col>
          </Row>
          <div style={{ fontSize: 12, color: "var(--secondary-text)",
                        marginTop: 6 }}>
            各站用不同的錯開分鐘，避免整點同時開啟串流。影像超過保留天數會自動
            刪除，數值永久保留。
          </div>
        </Form>
      </Col>
      <Col span={12}>
        <Divider orientation="left" plain>參數版本</Divider>
        <Table size="small" rowKey="id" pagination={{ pageSize: 6 }}
               dataSource={history}
               locale={{ emptyText: "尚無儲存紀錄" }}
               columns={[
                 { title: "時間", dataIndex: "saved_at",
                   render: (value) => new Date(value).toLocaleString([], {
                     month: "2-digit", day: "2-digit",
                     hour: "2-digit", minute: "2-digit" }) },
                 { title: "操作者", dataIndex: "saved_by",
                   render: (value) => value || "—" },
                 { title: "", width: 60,
                   render: (_, row) => (
                     <Button size="small" type="link"
                             onClick={() => restore(row)}>還原</Button>) },
               ]} />
      </Col>
    </Row>
  );

  const previewTab = (
    <div>
      {!preview && (
        <Empty description="尚未試算" style={{ padding: 40 }}>
          <Button type="primary" loading={busy === "preview"} onClick={runPreview}>
            以目前參數試算
          </Button>
        </Empty>
      )}
      {preview && preview.status !== "done" && (
        <Alert type="error" showIcon message="試算未完成"
               description={preview.error || preview.status} />
      )}
      {preview && preview.status === "done" && (
        <Row gutter={16}>
          <Col span={10}>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="水位">
                {fmt(preview.result?.water_level)} m
              </Descriptions.Item>
              <Descriptions.Item label="平均表面流速">
                {fmt(preview.result?.average_velocity, 3)} m/s
              </Descriptions.Item>
              <Descriptions.Item label="推估流量">
                {fmt(preview.result?.discharge)} cms
              </Descriptions.Item>
              <Descriptions.Item label="可信度">
                {preview.result?.quality?.trusted
                  ? <Tag color="success">通過</Tag>
                  : <Tag color="warning">低可信度</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="有效向量">
                {pct(preview.result?.quality?.valid_ratio)}
              </Descriptions.Item>
              <Descriptions.Item label="相關係數">
                {fmt(preview.result?.quality?.mean_corr, 3)}
              </Descriptions.Item>
              <Descriptions.Item label="位移中位數">
                {fmt(preview.result?.quality?.median_disp_px, 2)} px
              </Descriptions.Item>
              <Descriptions.Item label="GRP RMSE">
                {fmt(preview.result?.diagnostics?.ortho?.reproj_rmse_px, 2)} px
              </Descriptions.Item>
            </Descriptions>
            {(preview.result?.quality?.warnings || []).length > 0 && (
              <Alert style={{ marginTop: 10 }} type="warning" showIcon
                     message="品質警告"
                     description={
                       <ul style={{ margin: 0, paddingLeft: 16 }}>
                         {preview.result.quality.warnings.map((text) => (
                           <li key={text}>{text}</li>
                         ))}
                       </ul>} />
            )}
          </Col>
          <Col span={14}>
            <Space direction="vertical" style={{ width: "100%" }}>
              {preview.media?.velocity && (
                <img alt="流速向量" src={preview.media.velocity}
                     style={{ width: "100%", borderRadius: 4 }} />
              )}
              {preview.media?.waterlevel && (
                <img alt="水位" src={preview.media.waterlevel}
                     style={{ width: "100%", borderRadius: 4 }} />
              )}
            </Space>
          </Col>
        </Row>
      )}
    </div>
  );

  const grpTabLabel = usable.length === grps.length
    ? `正射 / GRP (${grps.length})`
    : `正射 / GRP (${usable.length}/${grps.length})`;

  return (
    <Modal
      title={`計算設定 — ${station?.mobile_name || machineID}`}
      open={open} width={1320} onCancel={onClose}
      maskClosable={false}
      footer={[
        <span key="hint" style={{ float: "left", fontSize: 12,
                                  color: "var(--secondary-text)" }}>
          {dirty ? "有尚未儲存的修改" : ""}
        </span>,
        <Button key="preview" loading={busy === "preview"} onClick={runPreview}>
          試算
        </Button>,
        <Button key="close" onClick={onClose}>關閉</Button>,
        <Button key="save" type="primary" loading={busy === "save"}
                disabled={!dirty} onClick={save}>儲存</Button>,
      ]}
    >
      <Tabs activeKey={tab} onChange={setTab} items={[
        { key: "ortho", label: grpTabLabel, children: orthoTab },
        { key: "waterlevel", label: "水位", children: waterlevelTab },
        { key: "piv", label: "PIV 格網", children: pivTab },
        { key: "flow", label: "流速 / 流量", children: flowTab },
        { key: "schedule", label: "排程 / 版本", children: scheduleTab },
        { key: "preview", label: "試算結果", children: previewTab },
      ]} />
      {surveyOpen && (
        <SurveyModal open={surveyOpen} onClose={() => setSurveyOpen(false)}
                     grps={grps} pixels={surveyPixels}
                     survey={params.survey}
                     onChange={(next) => update("survey", next)}
                     onApply={applySurvey} />
      )}
    </Modal>
  );
}

function NumberField({ label, value, onChange, ...rest }) {
  return (
    <div>
      <div style={{ fontSize: 12, marginBottom: 2 }}>{label}</div>
      <InputNumber size="small" style={{ width: "100%" }} value={value}
                   onChange={onChange} {...rest} />
    </div>
  );
}

function fmt(value, digits = 2) {
  return value === null || value === undefined || Number.isNaN(Number(value))
    ? "—" : Number(value).toFixed(digits);
}

function pct(value) {
  return value === null || value === undefined ? "—"
    : `${Math.round(Number(value) * 100)}%`;
}